<template>
  <div class="mx-auto w-full max-w-full min-h-0 box-border px-2 sm:px-0">
    <div v-if="loading" class="flex flex-col items-center justify-center min-h-[40vh] gap-3">
      <ProgressSpinner
        class="!w-12 !h-12"
        strokeWidth="4"
        aria-hidden="true"
        :pt="{
          root: { class: 'text-[var(--color-orange)]' },
        }"
      />
    </div>
    <Message
      v-else-if="!overview"
      severity="error"
      class="w-full max-w-xl mx-auto"
      :closable="false"
      :pt="{ root: { class: 'border-[var(--border-color)] rounded-lg' } }"
    >
      {{ $t('ProgressPage.loadError') }}
    </Message>
    <div v-else class="flex min-w-0 max-w-full flex-col gap-5 md:gap-6">
      <!-- KPI tiles -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <div v-for="(kpi, idx) in kpiCards" :key="kpi.key ?? idx"
          class="card p-4">
          <p class="text-base font-semibold text-[var(--color-black)] m-0 mb-1">
            {{ kpi.label }}
          </p>
          <p
            v-if="kpi.ineligible"
            class="text-sm text-[var(--color-black)] m-0 mt-1 break-words"
            role="status"
          >
            {{ kpi.ineligibleMessage }}
          </p>
          <template v-else>
            <p class="text-3xl md:text-4xl font-bold m-0" :class="kpi.valueClass">
              {{ kpi.value }}
            </p>
            <p class="text-sm text-[var(--color-inactive-dark)] m-0 mt-1 break-words">
              {{ kpi.sub }}
            </p>
          </template>
        </div>
      </div>

      <!-- Gap to organisation #1 (development points path across levels) -->
      <div v-if="overview.gapToTopSameLevelXp > 0">
        <div class="card flex flex-row items-center gap-3">
          <i
            class="pi pi-trophy text-[var(--color-orange)]" style="font-size: 2rem;"/>
          <div class="flex flex-col gap-1">
            <p class="text-base font-semibold text-[var(--color-black)] m-0 mb-1">
              {{ $t('ProgressPage.gapBanner') }}
            </p>
            <p class="m-0 text-sm md:text-base text-[var(--color-black)] flex-1 min-w-0">
              {{ $t('ProgressPage.gapBannerBodyBeforeY') }}
              <span class="font-semibold text-[var(--color-orange)]">{{
                gapBannerLeaderPoints
              }}</span>
              {{ $t('ProgressPage.gapBannerBodyBetweenYAndX') }}
              <span class="font-semibold text-[var(--color-orange)]">{{
                gapBannerPointsBehind
              }}</span>
              {{ $t('ProgressPage.gapBannerBodyAfterX') }}
            </p>
          </div>
        </div>
      </div>


      <!-- Organisation position bar: cohort = active users with level progress; metric = total development points through active level (API). -->
      <div v-if="showRankings && dist" class="card p-4 md:p-5">
        <div class="mb-3">
          <p class="text-base font-bold tracking-wide text-[var(--color-black)] m-0">
            {{ $t('ProgressPage.levelPositionTitle') }}
          </p>
          <p class="text-sm text-[var(--color-inactive-dark)] m-0 mt-1">
            {{ $t('ProgressPage.levelPositionSub') }}
          </p>
        </div>
        <div class="relative w-full">
          <div class="relative h-10 w-full">
            <div
              class="flex h-10 w-full min-h-0 overflow-hidden rounded-lg ring-1 ring-inset ring-[var(--border-color)]"
            >
              <div
                v-for="(frac, i) in dist.segmentFractions"
                :key="i"
                class="h-full min-w-0 flex flex-col justify-end border-r border-[var(--color-white)]/80 last:border-r-0 pb-0.5 pl-0.5 pr-0.5"
                :class="segmentClass(i)"
                :style="{ width: `${frac * 100}%` }"
              >
                <span
                  class="block text-[10px] sm:text-xs uppercase tracking-wide text-[var(--color-inactive-dark)] truncate leading-tight"
                >
                  {{ segmentLabel(i) }}
                </span>
              </div>
            </div>
            <div
              class="pointer-events-none absolute inset-0 z-[5] overflow-visible"
              aria-hidden="true"
            >
              <template v-for="m in markerDefs" :key="m.key">
                <div
                  v-if="m.variant === 'mean'"
                  class="absolute top-0 z-[5] h-10 w-px -translate-x-1/2 bg-zinc-600"
                  :style="{ left: `${m.pct * 100}%` }"
                />
                <div
                  v-else-if="m.variant === 'top'"
                  class="absolute top-0 z-[5] h-10 w-px -translate-x-1/2 bg-rose-500"
                  :style="{ left: `${m.pct * 100}%` }"
                />
                <div
                  v-else
                  class="absolute -top-1 z-[15] flex h-10 w-4 -translate-x-1/2 justify-center"
                  :style="{ left: `${m.pct * 100}%` }"
                >
                  <div class="relative h-full w-0">
                    <div
                      class="absolute left-1/2 top-0 h-full w-0 -translate-x-1/2 border-l-2 border-dashed border-[var(--color-orange)]"
                    />
                    <div
                      class="absolute left-1/2 top-1/2 z-10 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[var(--color-white)] bg-[var(--color-orange)] shadow-sm"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>
          <!-- Segment people counts (hidden when solo org cohort: no meaningful quartile split) -->
          <ul
            v-if="dist.segmentCounts && !isSoloLevelCohort"
            class="m-0 mt-2 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-[10px] sm:text-xs text-[var(--color-inactive-dark)]"
          >
            <li
              v-for="(count, i) in dist.segmentCounts"
              :key="'seg-count-' + i"
              class="flex items-center gap-1.5"
            >
              <span
                class="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                :class="segmentClass(i)"
                aria-hidden="true"
              />
              <span>{{ segmentLabel(i) }}: {{ $t('ProgressPage.segmentPeopleCount', { n: count }) }}</span>
            </li>
          </ul>
          <div
            v-else-if="isSoloLevelCohort"
            class="m-0 mt-2 flex flex-col gap-1.5 p-0 text-xs sm:text-sm text-[var(--color-inactive-dark)]"
          >
            <p class="m-0 leading-snug">
              {{ $t('ProgressPage.levelPositionSoloIntro') }}
            </p>
            <p class="m-0 leading-snug">
              {{ $t('ProgressPage.levelPositionSoloBarNote') }}
            </p>
          </div>

          <!-- Marker legend (you, avg, top) -->
          <ul
            class="m-0 mt-3 flex list-none flex-wrap gap-x-4 gap-y-2 p-0 text-xs sm:text-sm"
            :aria-label="$t('ProgressPage.levelPositionLegendAria')"
          >
            <li
              v-for="m in markerDefs"
              :key="'leg-' + m.key"
              class="flex min-w-0 max-w-full justify-center items-center gap-2"
            >
              <span
                v-if="m.variant === 'you'"
                class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center"
                aria-hidden="true"
              >
                <span
                  class="h-2.5 w-2.5 rounded-full border-2 border-[var(--color-white)] bg-[var(--color-orange)] shadow-sm ring-1 ring-[var(--color-orange)]"
                />
              </span>
              <span
                v-else-if="m.variant === 'mean'"
                class="mt-0.5 h-4 w-px shrink-0 bg-zinc-600"
                aria-hidden="true"
              />
              <span v-else class="mt-0.5 h-4 w-px shrink-0 bg-rose-500" aria-hidden="true" />
              <span class="min-w-0 text-[var(--color-black)]">
                <span class="font-semibold">{{ m.legendTitle }}</span>
                <span class="block text-[var(--color-inactive-dark)] sm:ml-1 sm:inline">
                  {{ $t('ProgressPage.xpValue', { n: m.displayXp }) }}
                </span>
              </span>
            </li>
          </ul>
        </div>
      </div>
      <div
        v-else-if="!showRankings"
        class="card p-4 md:p-5"
        role="status"
      >
        <p class="text-base font-bold tracking-wide text-[var(--color-black)] m-0">
          {{ $t('ProgressPage.levelPositionTitle') }}
        </p>
        <p class="text-base text-[var(--color-black)] m-0 mt-3">
          {{ $t('LevelDashboard.rankingNotEligibleMessage') }}
        </p>
      </div>

      <!-- Roadmap timeline (level-ups, goals, workshops, features) -->
      <div class="card min-w-0 max-w-full p-4 md:p-5">
        <p class="text-base font-semibold text-[var(--color-black)] m-0 mb-4">
          {{ $t('ProgressPage.timelineTitle') }}
        </p>
        <PersonalJourneyTimelineComponent
          :events="timelineEvents"
          :maximum-level-reached="isMaximumLevelReached"
        />
      </div>

      <!-- Level-up / average / status -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="card p-4 flex flex-col items-start justify-center">
          <template v-if="isMaximumLevelReached">
            <p class="text-xl font-bold tracking-wide text-[var(--color-black)] m-0 mb-2">
              {{ $t('LevelDashboard.maximumLevelReached') }}
            </p>
            <p class="text-base text-[var(--color-black)] m-0">
              {{ $t('LevelDashboard.noFurtherLevelAvailable') }}
            </p>
          </template>
          <template v-else-if="isOnHighestConfiguredLevel">
            <p class="text-base font-semibold text-[var(--color-black)] m-0 mb-1">
              {{ $t('ProgressPage.levelUpTitle') }}
            </p>
            <p class="text-2xl md:text-3xl font-semibold text-[var(--color-orange)] m-0">
              {{ $t('ProgressPage.xpValue', { n: overview.xpRemaining }) }}
            </p>
            <p class="text-sm text-[var(--color-black)] m-0 mt-1">
              {{ $t('ProgressPage.topLevelXpSub') }}
            </p>
          </template>
          <template v-else>
            <p class="text-base font-semibold text-[var(--color-black)] m-0 mb-1">
              {{ $t('ProgressPage.levelUpTitle') }}
            </p>
            <p class="text-2xl md:text-3xl font-semibold text-[var(--color-orange)] m-0">
              {{ $t('ProgressPage.xpValue', { n: overview.xpRemaining }) }}
            </p>
            <p class="text-sm text-[var(--color-black)] m-0 mt-1">
              {{ $t('ProgressPage.untilLevel', { n: overview.nextLevel }) }}
            </p>
          </template>
        </div>
        <div class="card p-4 flex flex-col items-start justify-center">
          <p class="text-base font-semibold text-[var(--color-black)] m-0 mb-1">
            {{ $t('ProgressPage.avgTitle') }}
          </p>
          <p class="text-2xl md:text-3xl font-semibold text-[var(--color-black)] m-0">
            {{ $t('ProgressPage.xpValue', { n: overview.averageXpSameLevel }) }}
          </p>
          <p class="text-sm text-[var(--color-inactive-dark)] m-0 mt-1">
            {{ $t('ProgressPage.avgSub') }}
          </p>
        </div>
        <div class="card p-4 flex flex-row items-center justify-start gap-x-4">
          <i class="pi pi-thumbs-up text-[var(--color-orange)]" style="font-size: 2rem;"></i>
          <p class="text-sm md:text-base text-[var(--color-black)] min-w-0 leading-snug m-0">
            {{ personalJourneyFeedbackText }}
          </p>
        </div>
      </div>

      <!-- Individual ranking -->
      <div class="card p-4">
        <div class="flex flex-col gap-4 mb-2">
          <div class="flex flex-col sm:flex-row gap-3 justify-between items-center w-full">
            <p class="text-base font-semibold text-[var(--color-black)] m-0">
              {{ $t('ProgressPage.rankingTitle') }}
            </p>
            <!--SelectButton
                v-model="rankingScope"
                class="progress-ranking-scope shrink-0 text-sm md:text-base"
                :options="scopeOptions"
                optionLabel="label"
                optionValue="value"
                :allowEmpty="false"
              /-->
          </div>
          <div v-if="showTeamRankingDropdown" class="flex flex-start gap-4 flex-col sm:flex-row items-center">
            <h3 class="text-sm md:text-base text-[var(--color-black)] min-w-0 leading-snug m-0">
              {{ $t('TeamResultDashboardComponent.selectTeam') }}:
            </h3>
            <Select
              v-model="selectedTeamIdForRanking"
              :options="teamRankingSelectOptions"
              optionLabel="label"
              optionValue="value"
              :placeholder="$t('ProgressPage.rankingSelectTeamPlaceholder')"
              class="w-full max-w-md progress-ranking-team-select"
              :aria-label="$t('ProgressPage.rankingSelectTeamAria')"
            />
          </div>
        </div>
        <DataTable
          :value="activeRankingRows"
          size="small"
          class="progress-page-table text-sm w-full !bg-transparent"
          :rowClass="rankingRowClass"
          :pt="{
            root: { class: 'border-none w-full' },
            table: { class: 'w-full' },
            column: { headerCell: { class: 'text-[var(--color-inactive-dark)] font-semibold' } },
          }"
        >
          <Column :header="$t('ProgressPage.colRank')">
            <template #body="{ data }">
              <span class="font-medium text-[var(--color-black)]">#{{ data.rank }}</span>
            </template>
          </Column>
          <Column :header="$t('ProgressPage.colName')">
            <template #body="{ data }">
              <div class="flex items-center gap-2 min-w-0">
                <img
                  :src="rankingAvatarSrc(data)"
                  alt=""
                  class="h-8 w-8 shrink-0 rounded-full object-cover"
                  @error="onRankingAvatarError"
                />
                <span class="min-w-0 text-[var(--color-black)]">
                  {{ data.name || '—' }}
                  <span
                    v-if="data.isCurrentUser"
                    class="ml-1 text-xs font-semibold text-[var(--color-orange)]"
                  >
                    ({{ $t('ProgressPage.you') }})
                  </span>
                </span>
              </div>
            </template>
          </Column>
          <Column :header="$t('ProgressPage.colLevelXp')">
            <template #body="{ data }">
              <span class="text-[var(--color-black)]">
                {{ $t('ProgressPage.levelXpCell', { level: data.level, xp: formatXp(data.currentXP) }) }}
              </span>
            </template>
          </Column>
        </DataTable>
      </div>

      <!-- Team ranking -->
      <div class="card p-4">
        <p class="text-base font-semibold text-[var(--color-black)] m-0 mb-4">
          {{ $t('ProgressPage.teamRankingTitle') }}
        </p>
        <DataTable
          :value="teamRankingRows"
          :paginator="teamRankingRows.length > 10"
          :rows="10"
          size="small"
          class="progress-page-table text-sm w-full !bg-transparent"
          :rowClass="teamRowClass"
          :pt="{
            root: { class: 'border-none w-full' },
            table: { class: 'w-full' },
            column: { headerCell: { class: 'text-[var(--color-inactive-dark)] font-semibold' } },
          }"
        >
          <Column :header="$t('ProgressPage.colRank')">
            <template #body="{ data }">
              <span class="font-medium text-[var(--color-black)]">#{{ data.rank }}</span>
            </template>
          </Column>
          <Column :header="$t('ProgressPage.colTeam')">
            <template #body="{ data }">
              <span class="text-[var(--color-black)]">
                {{ data.teamName }}
                <span
                  v-if="data.isUsersTeam"
                  class="ml-1 text-xs font-semibold text-[var(--color-orange)]"
                >
                  ({{ $t('ProgressPage.yourTeam') }})
                </span>
              </span>
            </template>
          </Column>
          <Column :header="$t('ProgressPage.colTeamScore')">
            <template #body="{ data }">
              <span class="text-[var(--color-black)]">
                {{
                  $t('ProgressPage.teamScoreCell', {
                    xp: formatXp(data.teamScore),
                    n: data.memberCountWithProgress,
                  })
                }}
              </span>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import SelectButton from 'primevue/selectbutton';
import Select from 'primevue/select';
import ProgressSpinner from 'primevue/progressspinner';
import Message from 'primevue/message';
import defaultProfileImage from '@/assets/defaultProfileImage.js';
import PersonalJourneyTimelineComponent from './PersonalJourneyTimelineComponent.vue';

export default {
  name: 'PersonalJourneyComponent',
  components: {
    Button,
    DataTable,
    Column,
    SelectButton,
    Select,
    ProgressSpinner,
    Message,
    PersonalJourneyTimelineComponent,
  },
  data() {
    return {
      loading: true,
      rankingScope: 'organization',
      /** When the user has multiple teams, which team’s member ranking table to show (team scope). */
      selectedTeamIdForRanking: null,
    };
  },
  computed: {
    overview() {
      return this.$store.getters.getLevelProgressOverview;
    },
    /** Events for PersonalJourneyTimelineComponent (from GET personal-journey API). */
    timelineEvents() {
      const raw = this.$store.getters.getTimelineData;
      return Array.isArray(raw) ? raw : [];
    },
    dist() {
      return this.overview?.levelDistribution;
    },
    showRankings() {
      const o = this.overview;
      if (!o) return true;
      return o.rankingEligible !== false;
    },
    /** Organisation cohort from API: only one active user with progress → no peer comparison bar semantics. */
    isSoloLevelCohort() {
      const d = this.dist;
      if (!d || typeof d.cohortSize !== 'number') return false;
      return d.cohortSize === 1;
    },
    gapBannerLeaderPoints() {
      const o = this.overview;
      if (!o) return 0;
      return Math.round(o.organizationRankOneTotalXp ?? o.topSameLevelXp ?? 0);
    },
    gapBannerPointsBehind() {
      return Math.round(this.overview?.gapToTopSameLevelXp ?? 0);
    },
    scopeOptions() {
      return [
        { label: this.$t('ProgressPage.scopeOrg'), value: 'organization' },
        { label: this.$t('ProgressPage.scopeTeam'), value: 'team' },
      ];
    },
    kpiCards() {
      if (!this.overview) return [];
      const o = this.overview;
      const rankingIneligible = !this.showRankings;
      return [
        {
          key: 'level',
          label: this.$t('ProgressPage.kpiLevel'),
          value: o.currentLevel || '—',
          valueClass: 'text-[var(--color-orange)]',
          sub: this.$t('ProgressPage.kpiLevelSub', { total: o.totalLevels || 0 }),
        },
        {
          key: 'ranking',
          label: this.$t('ProgressPage.kpiRanking'),
          ineligible: rankingIneligible,
          ineligibleMessage: this.$t('LevelDashboard.rankingNotEligibleMessage'),
          value: `#${o.organizationRank}`,
          valueClass: 'text-[var(--color-black)]',
          sub: this.$t('ProgressPage.kpiRankingSub', { total: o.organizationTotal }),
        },
        {
          key: 'week',
          label: this.$t('ProgressPage.kpiWeek'),
          value: `+${this.formatXp(o.weeklyXpThisWeek)}`,
          valueClass: 'text-[var(--color-orange)]',
          sub: this.$t('ProgressPage.kpiWeekSub'),
        },
        {
          key: 'streak',
          label: this.$t('ProgressPage.kpiStreak'),
          value: String(o.consecutiveActiveWeeks),
          valueClass: 'text-[var(--color-black)]',
          sub: this.$t('ProgressPage.kpiStreakSub'),
        },
      ];
    },
    teamMemberRankingsByTeam() {
      const list = this.overview?.teamMemberRankingsByTeam;
      return Array.isArray(list) ? list : [];
    },
    /** Dropdown options when the viewer belongs to multiple teams and uses team scope. */
    teamRankingSelectOptions() {
      return this.teamMemberRankingsByTeam.map((g) => ({
        label: g.teamName?.trim() ? g.teamName : g.teamId,
        value: g.teamId,
      }));
    },
    showTeamRankingDropdown() {
      return (
        this.rankingScope === 'team'
        && this.teamMemberRankingsByTeam.length > 1
      );
    },
    activeRankingRows() {
      if (!this.overview) return [];
      if (this.rankingScope === 'organization') {
        const rows = this.overview.rankingOrganization;
        return Array.isArray(rows) ? rows : [];
      }
      const byTeam = this.teamMemberRankingsByTeam;
      if (byTeam.length > 0) {
        if (byTeam.length === 1) {
          const r = byTeam[0].rankingRows;
          return Array.isArray(r) ? r : [];
        }
        const tid = this.selectedTeamIdForRanking;
        const group = byTeam.find((g) => g.teamId === tid);
        if (group && Array.isArray(group.rankingRows)) {
          return group.rankingRows;
        }
        const fallback = byTeam[0]?.rankingRows;
        return Array.isArray(fallback) ? fallback : [];
      }
      const rows = this.overview.rankingTeam;
      return Array.isArray(rows) ? rows : [];
    },
    teamRankingRows() {
      const tr = this.overview?.teamRanking;
      return Array.isArray(tr) ? tr : [];
    },
    markerDefs() {
      if (!this.dist || !this.overview) return [];
      const d = this.dist;
      /** Same scale as cohort / bar: total development points through each person’s active level (API). */
      const viewerXp = Math.round(
        Number(
          this.overview.viewerTotalXpThroughActiveLevel
            ?? this.overview.viewerCurrentXp
            ?? 0,
        ),
      );
      const meanXp = Math.round(d.meanXp);
      const topXp = Math.round(d.topXp);
      const you = {
        key: 'you',
        variant: 'you',
        pct: d.markerYou,
        legendTitle: this.$t('ProgressPage.markerYou'),
        displayXp: viewerXp,
      };
      if (d.cohortSize === 1) {
        return [you];
      }
      return [
        {
          key: 'mean',
          variant: 'mean',
          pct: d.markerMean,
          legendTitle: this.$t('ProgressPage.markerAvg'),
          displayXp: meanXp,
        },
        you,
        {
          key: 'top',
          variant: 'top',
          pct: d.markerTop,
          legendTitle: this.$t('ProgressPage.markerTop'),
          displayXp: topXp,
        },
      ];
    },
    /** Matches LevelDashboardSection: full progress on last configured level (store vs overview totals). */
    isMaximumLevelReached() {
      const o = this.$store.getters.getLevelProgressOverview;
      if (!o || typeof o.totalLevels !== 'number' || o.totalLevels <= 0) {
        return false;
      }
      const totalXP = this.$store.getters.getTotalXP;
      const currentXP = this.$store.getters.getCurrentXP;
      const xpRemaining = Math.max(0, totalXP - currentXP);
      const progressPercent = !totalXP || totalXP <= 0
        ? 0
        : Math.min(100, Math.round((currentXP / totalXP) * 100));
      return (
        o.currentLevel >= o.totalLevels
        && totalXP > 0
        && xpRemaining === 0
        && progressPercent >= 100
      );
    },
    /** On last configured level number but may still have open goals / progress (do not show “next level n+1”). */
    isOnHighestConfiguredLevel() {
      const o = this.overview;
      if (!o || typeof o.totalLevels !== 'number' || o.totalLevels <= 0) return false;
      const cur = Number(o.currentLevel);
      if (!Number.isFinite(cur)) return false;
      return cur >= o.totalLevels;
    },
    /** Thumbs-up feedback (priority: max level → recent level-up → weekly development points → percentile → no activity). */
    personalJourneyFeedbackText() {
      const o = this.overview;
      if (!o) return '';

      if (this.feedbackIsMaxLevel) {
        return this.$t('ProgressPage.feedbackMaxLevel');
      }
      if (this.feedbackLevelJustCompleted) {
        return this.$t('ProgressPage.feedbackLevelCompleted');
      }

      const weekly = Number(o.weeklyXpThisWeek) || 0;
      if (weekly === 0) {
        return this.$t('ProgressPage.feedbackNoActivityWeek');
      }

      const p = o.percentileAheadOfLevelPeers;
      if (p != null) {
        if (p >= 75) return this.$t('ProgressPage.feedbackPercentileTop');
        if (p >= 50) return this.$t('ProgressPage.feedbackPercentileMid');
        return this.$t('ProgressPage.feedbackPercentileLow');
      }

      return '';
    },
    /** Max-level congratulations only when on last level and all current goals are done (and progress bar full). */
    feedbackIsMaxLevel() {
      if (!this.isMaximumLevelReached) return false;
      const goals = this.$store.getters.getLevelInfo || [];
      if (goals.length === 0) return true;
      return goals.every((g) => g.completed === true);
    },
    /**
     * Encouragement after advancing to a new level: show while the user still has 0 development points on the current
     * level (not time-bound). Excludes level 1 (new users) and the highest configured level with open
     * goals, where “completed your level” would be misleading.
     */
    feedbackLevelJustCompleted() {
      const o = this.overview;
      if (!o) return false;
      if (this.isOnHighestConfiguredLevel) return false;
      const xpOnCurrentLevel = Math.round(Number(o.viewerCurrentXp ?? 0));
      if (xpOnCurrentLevel !== 0) return false;
      const cur = Number(o.currentLevel);
      return Number.isFinite(cur) && cur >= 2;
    },
  },
  watch: {
    overview: {
      immediate: true,
      handler(o) {
        const list = o?.teamMemberRankingsByTeam;
        if (!Array.isArray(list) || list.length === 0) {
          this.selectedTeamIdForRanking = null;
          return;
        }
        const ids = new Set(list.map((x) => x.teamId));
        if (
          !this.selectedTeamIdForRanking
          || !ids.has(this.selectedTeamIdForRanking)
        ) {
          this.selectedTeamIdForRanking = list[0].teamId;
        }
      },
    },
  },
  async mounted() {
    this.loading = true;
    try {
      await Promise.all([
        this.$store.dispatch('fetchLevelProgressOverview'),
        this.$store.dispatch('fetchPersonalJourneyData').catch(() => {}),
      ]);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    formatXp(n) {
      const v = Number(n);
      if (!Number.isFinite(v)) return '0';
      return Math.round(v).toLocaleString(this.$i18n?.locale || 'en');
    },
    segmentLabel(i) {
      const keys = [
        'ProgressPage.segmentLowerQ',
        'ProgressPage.segmentMid',
        'ProgressPage.segmentUpperQ',
        'ProgressPage.segmentTop10',
      ];
      return this.$t(keys[i] ?? keys[0]);
    },
    segmentClass(i) {
      const classes = [
        'pj-pos-seg-lower',
        'pj-pos-seg-mid',
        'pj-pos-seg-upper',
        'pj-pos-seg-top10',
      ];
      return classes[i] || 'pj-pos-seg-lower';
    },
    rankingRowClass(row) {
      return row.isCurrentUser ? 'progress-page-row--self' : 'progress-page-row--other';
    },
    teamRowClass(row) {
      return row.isUsersTeam ? 'progress-page-row--self' : 'progress-page-row--other';
    },
    rankingAvatarSrc(row) {
      const fromApi = row.profilePictureSmall;
      if (fromApi) return fromApi;
      if (row.isCurrentUser) {
        const small = this.$store.getters.getUser?.picture_profile?.small;
        if (small) return small;
      }
      return defaultProfileImage;
    },
    onRankingAvatarError(event) {
      const el = event?.target;
      if (el && el instanceof HTMLImageElement) {
        el.src = defaultProfileImage;
      }
    },
  },
};
</script>

<style scoped>
/* Ranking scope SelectButton: orange text on the selected segment */
:deep(.progress-ranking-scope .p-togglebutton-checked .p-togglebutton-label) {
  color: var(--color-orange) !important;
}

/*
 * Match .card surface (#f4f4f4); PrimeVue defaults table/header cells to white.
 * Only the highlighted row (you / your team) uses --color-answer-card.
 */
:deep(.progress-page-table .p-datatable-thead > tr > th) {
  background-color: #f4f4f4 !important;
}

:deep(.progress-page-table .p-datatable-tbody > tr > td) {
  background-color: #f4f4f4 !important;
}

:deep(.progress-page-table .p-datatable-tbody > tr.progress-page-row--self),
:deep(.progress-page-table .p-datatable-tbody > tr.progress-page-row--self > td) {
  background-color: var(--color-answer-card) !important;
}

:deep(.progress-page-table .p-paginator) {
  background-color: #f4f4f4;
  border-color: var(--border-color);
}

/* Organisation position bar — pastel bands (total development points through active level, cohort from API) */
.pj-pos-seg-lower {
  background-color: var(--color-light-grey);
}

.pj-pos-seg-mid {
  background-color: #dbeafe;
}

.pj-pos-seg-upper {
  background-color: #ffedd5;
}

.pj-pos-seg-top10 {
  background-color: #fce7f3;
}
</style>
